import FilterChip from 'LuxKit/Chips/FilterChip'
import DropdownChevron from 'LuxKit/Dropdown/DropdownChevron'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import RadioInputListItem from 'LuxKit/List/RadioInputListItem'
import FormModal from 'LuxKit/Modal/FormModal'
import ModalContext from 'contexts/ModalContext'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import useToggle from 'hooks/useToggle'
import { capitalise } from 'lib/string/stringUtils'
import { useCallback, useContext, useRef } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'

export const getMerchandiseSearchSortOptions = (intl: ReturnType<typeof useIntl>): Array<{ label: string, value: App.MerchandiseSearchSort }> => [
  {
    label: intl.formatMessage({
      id: 'O-dj9x-We6JSdcO9S8HD',
      defaultMessage: 'Recommended',
      description: 'Sort option label for recommended sorting in the merchandise shop section',
    }),
    value: 'recommended',
  },
  {
    label: intl.formatMessage({
      id: 'o172EKXpDI6ZqDIYpcXs',
      defaultMessage: 'New Arrivals',
      description: 'Sort option label for new arrivals sorting in the merchandise shop section',
    }),
    value: 'newArrivals',
  },
  {
    label: intl.formatMessage({
      id: 'l_9P0wZhd_Rh89bvfIxR',
      defaultMessage: 'Price (lowest)',
      description: 'Sort option label for lowest price sorting in the price breakdown',
    }),
    value: 'priceAsc',
  },
  {
    label: intl.formatMessage({
      id: 'ZcrdvdEr6dPpYRPX8tFi',
      defaultMessage: 'Price (highest)',
      description: 'Sort option label for highest price sorting in the price breakdown',
    }),
    value: 'priceDesc',
  },
]

interface Props {
  /**
   * The current sort by value
   *
   * @default recommended
   */
  sortBy?: App.MerchandiseSearchSort
  onChange?: (sortBy: App.MerchandiseSearchSort) => void
}

function MerchandiseSearchPageSort(props: Props) {
  const { sortBy = 'recommended', onChange } = props
  const intl = useIntl()
  const [openDropdown, toggleDropdown, _, closeDropdown] = useToggle()

  const anchorRef = useRef<HTMLButtonElement>(null)
  const showModal = useContext(ModalContext)
  const isMobile = useScreenSizeOnly('mobile')

  const sortOptions = getMerchandiseSearchSortOptions(intl)

  const sortByModalTitle = intl.formatMessage({
    id: 'VP2ku0rl4zDel8YpD-Ap',
    defaultMessage: 'Sort by',
    description: 'Modal title for the sort by options dialog in the search results interface',
  })

  const sortByLabel = intl.formatMessage({
    id: 'LQzCFcsbdVjs_d1GjS1i',
    defaultMessage: 'Sort',
    description: 'Label prefix for sort filter chip in the search or listing filters panel',
  })

  const onFilterClick = useCallback(async() => {
    if (isMobile) {
      const result = await showModal<{ sortBy: App.MerchandiseSearchSort }>(resolve => <FormModal title={sortByModalTitle} flush>
        {sortOptions.map((option) => <RadioInputListItem
          key={option.value}
          name="sortBy"
          title={option.label}
          value={option.value}
          defaultChecked={sortBy === option.value}
          onChange={e => resolve({ sortBy: e.target.value as App.MerchandiseSearchSort })}
          inputPlacement="start"
        />)}
      </FormModal>)

      if (result?.sortBy) {
        onChange?.(result.sortBy)
      }
    } else {
      toggleDropdown()
    }
  }, [isMobile, onChange, showModal, sortBy, toggleDropdown, sortByModalTitle, sortOptions])

  const onSortChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(e.target.value as App.MerchandiseSearchSort)
    closeDropdown()
  }, [onChange, closeDropdown])

  const selectedSortLabel = sortOptions.find(option => option.value === sortBy)?.label ?? ''

  return <>
    <FilterChip
      size="medium"
      ref={anchorRef}
      onClick={onFilterClick}
      endIcon={<DropdownChevron open={openDropdown} />}
    >
      {intl.formatMessage(
        {
          id: 'Xnn-Db6I4kT7M-JpvxT8',
          defaultMessage: '{sortBy}: {selected}',
          description: 'Sort filter chip showing current sort label and selected option',
        },
        { sortBy: sortByLabel, selected: capitalise(selectedSortLabel) },
      )}
    </FilterChip>
    <DropdownList
      open={openDropdown}
      anchorRef={anchorRef}
      triggerRef={anchorRef}
      size="S"
      onClose={closeDropdown}
    >
      {sortOptions.map((option) => <RadioInputListItem
        key={option.value}
        name="sortBy"
        title={option.label}
        value={option.value}
        defaultChecked={sortBy === option.value}
        inputPlacement="start"
        onChange={onSortChange}
      />)}
    </DropdownList>
  </>
}

export default MerchandiseSearchPageSort
